<template>
  <v-dialog
    class="user-list"
    v-model="show"
    max-width="800"
    @click:outside="close"
  >
    <v-card>
      <v-card-title class="headline">View Site Sparkies</v-card-title>
      <v-card-text class="mt-5">
        <v-text-field
          v-model="form.search"
          flat
          solo
          required
          placeholder="Search sparkies"
          class="mt-2"
          :append-icon="icons.search"
          @input="onSearch"
          @keydown.enter="fetchUsers"
        ></v-text-field>
        <v-simple-table class="user-list__table">
          <thead>
            <tr>
              <th class="text-left">Name</th>
              <th class="text-left">Email</th>
              <th class="text-center">Action(s)</th>
            </tr>
          </thead>
          <tbody v-if="!form.$busy">
            <tr v-for="user in sparkies" :key="user.id">
              <td class="py-4">{{ user.full_name }}</td>
              <td class="py-4">{{ user.email }}</td>
              <td class="py-4 d-flex justify-center action-cell">
                <template v-if="toggledUser !== user">
                  <v-btn
                    x-small
                    color="red"
                    dark
                    depressed
                    @click="setToggledUser(user)"
                  >
                    Remove
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                    class="mr-2"
                    x-small
                    color="red"
                    dark
                    depressed
                    @click="removeUser"
                  >
                    Confirm
                  </v-btn>
                  <v-btn
                    x-small
                    color="primary"
                    dark
                    depressed
                    @click="setToggledUser(null)"
                  >
                    Cancel
                  </v-btn>
                </template>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="index in 5" :key="index + '-skeleton'">
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-pagination
          v-if="sparkiesMeta && sparkiesMeta.last_page > 1"
          class="pt-4"
          :value="sparkiesMeta.current_page"
          :length="paginationLength"
          :total-visible="paginationLimit"
          @input="onPageChange"
        ></v-pagination>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { mdiMinusCircle, mdiMagnify } from '@mdi/js'
import { mapState, mapMutations, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import Form from '@/utils/form'

export default {
  props: {
    paginationLimit: {
      type: Number,
      default: 6
    }
  },
  data() {
    return {
      site: null,
      toggledUser: null,
      show: false,
      form: new Form({
        search: ''
      }),
      icons: {
        delete: mdiMinusCircle,
        search: mdiMagnify
      },
      paginationPage: 1,
      snackbar: {
        show: false,
        message: null,
        color: ''
      }
    }
  },
  computed: {
    ...mapState({
      sparkies: state => state.user.list,
      sparkiesMeta: state => state.user.listMeta
    }),

    paginationLength() {
      return (this.sparkiesMeta.total / this.sparkiesMeta.per_page + 1) | 0
    }
  },
  methods: {
    ...mapMutations({
      clearSparkies: 'user/clearUserList'
    }),

    ...mapActions({
      getUsers: 'user/getUsers',
      removeSparkie: 'site/removeSparkieToSite'
    }),

    open(site) {
      if (!site) return

      this.show = true
      this.site = site
      this.fetchUsers()
    },

    close() {
      this.show = false
    },

    onSearch: debounce(function() {
      this.fetchUsers(1)
    }, 600),

    onPageChange(page) {
      this.fetchUsers(page)
    },

    async fetchUsers(page) {
      if (this.form.$busy) return

      this.setToggledUser(null)
      this.clearSparkies()
      this.form.$setLoading(true)

      this.paginationPage = page || this.paginationPage

      await this.getUsers({
        page: this.paginationPage,
        limit: this.paginationLimit,
        search: this.form.search,
        site: this.site.site_id
      })
        .catch(error => {
          this.form.$handleError(error)
          this.showSnackbar(this.form.$errorMessage, 'red')
        })
        .finally(() => {
          this.form.$setLoading(false)
        })
    },

    async removeUser() {
      if (this.form.$busy || !this.toggledUser) return

      this.form.$setLoading(true)
      await this.removeSparkie({
        site: this.site.site_id,
        user: this.toggledUser.id
      })
        .then(() => {
          this.form.$setLoading(false)
          this.fetchUsers()

          this.showSnackbar('User removed from site!', 'success')
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)

          this.showSnackbar(this.form.$errorMessage, 'red')
        })
    },

    setToggledUser(user) {
      this.toggledUser = user
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    }
  }
}
</script>

<style lang="scss" scoped>
.user-list {
  &__table {
    min-height: 250px;
    max-height: 400px;

    tbody {
      .action-cell {
        width: 200px;
        margin: 0 auto;
      }
    }
  }
}
</style>
